import { api, ApiError } from './client';
export type BidSaveState=Readonly<{id:number;phase:'queued'|'sending'|'waiting'|'applied'|'failed';value:string;message?:string}>;
export type ServerBidJob={accepted:true;id:string;sequence:string;campaignId:string;nmId:number;kind:'product'|'cluster'|'cluster_remove';
  selector:string;bidKopecks:number|null;confirm:boolean;state:'pending'|'running'|'uncertain'|'confirmed'|'rejected'|'cancelled';
  code?:string;message?:string;result?:Record<string,unknown>|null};
type Options={method:'PATCH'|'DELETE';body:Record<string,unknown>};
type Group={base:string;campaign:string;kind:'product'|'cluster';refs:number;polling:boolean;nextAt?:number};
const pause=(ms:number)=>new Promise<void>(resolve=>setTimeout(resolve,ms));
export function bidSaveKey(path:string,body:Record<string,unknown>):string {return JSON.stringify([path,body.nmId,body.placement??body.normQuery]);}
function location(path:string) {
  const m=path.match(/^(\/workspaces\/[^/]+\/cabinets\/[^/]+)\/campaigns\/([^/]+)\/(bids|cluster-bids)$/);
  if(!m)throw new Error('Не определён кабинет очереди ставок.');
  return {base:m[1]!,campaign:m[2]!,kind:m[3]==='bids'?'product' as const:'cluster' as const};
}
/** Admission is serialized only until PostgreSQL commits. Execution and polling never hold admission. */
export class BidSaveQueue {
  private states=new Map<string,BidSaveState>();
  private groups=new Map<string,Group>();
  private lanes=new Map<string,Promise<unknown>>();
  private listeners=new Set<()=>void>();
  private pendingJobs=new Map<string,{key:string;localId:number;epoch:number;resolve:(v:unknown)=>void;reject:(e:unknown)=>void;group:string}>();
  private latest=new Map<string,{value:string;promise:Promise<unknown>}>();
  private serverSequence=new Map<string,bigint>();
  private confirmedGroups=new Map<string,bigint>();
  private timer:ReturnType<typeof setInterval>|undefined;
  private sequence=0;private epoch=0;private admissions=0;
  constructor(private request:typeof api=api,private wait=pause,private uuid:()=>string=()=>crypto.randomUUID(),private pendingChanged:(n:number)=>void=()=>{}) {}
  subscribe=(listener:()=>void)=>{this.listeners.add(listener);return()=>{this.listeners.delete(listener);};};
  state=(key:string)=>this.states.get(key);
  confirmedSequence(path:string):string {const l=location(path);return String(this.confirmedGroups.get(JSON.stringify([l.base,l.campaign,l.kind]))??0n);}
  get pending():number{return this.admissions;}
  private emit(){for(const l of this.listeners)l();}
  private ensureTimer(){if(!this.timer)this.timer=setInterval(()=>{void this.poll(false);},2000);}
  watch(path:string):()=>void {
    const l=location(path),key=JSON.stringify(l);const g=this.groups.get(key)??{...l,refs:0,polling:false};
    g.refs++;this.groups.set(key,g);this.ensureTimer();void this.pollGroup(key,g);
    return()=>{g.refs--;this.prune();};
  }
  private prune(){
    for(const [k,g]of this.groups)if(g.refs===0&&![...this.pendingJobs.values()].some(p=>p.group===k))this.groups.delete(k);
    if(!this.groups.size&&this.timer){clearInterval(this.timer);this.timer=undefined;}
  }
  invalidateSession():void {
    this.epoch++;this.states.clear();this.latest.clear();this.serverSequence.clear();this.confirmedGroups.clear();
    for(const p of this.pendingJobs.values())p.reject(new Error('Вход изменён. Принятые сервером ставки продолжат выполняться.'));
    this.pendingJobs.clear();this.groups.clear();if(this.timer)clearInterval(this.timer);this.timer=undefined;this.emit();
  }
  enqueue<T>(path:string,options:Options):Promise<T> {
    const l=location(path),body=JSON.parse(JSON.stringify(options.body)) as Record<string,unknown>;
    const key=bidSaveKey(path,body),value=JSON.stringify([options.method,body]);
    const old=this.latest.get(key);if(old?.value===value)return old.promise as Promise<T>;
    if(this.admissions>=500)return Promise.reject(new Error('Ожидают приёма уже 500 ставок.'));
    const id=this.uuid(),localId=++this.sequence,epoch=this.epoch,group=JSON.stringify(l);
    this.states.set(key,{id:localId,phase:'sending',value,message:'Сохраняется на сервере…'});this.pendingChanged(++this.admissions);this.emit();
    let resolve!:(v:unknown)=>void,reject!:(e:unknown)=>void;
    const promise=new Promise<unknown>((yes,no)=>{resolve=yes;reject=no;});
    this.pendingJobs.set(id,{key,localId,epoch,resolve,reject,group});this.latest.set(key,{value,promise});
    const g=this.groups.get(group)??{...l,refs:0,polling:false};g.nextAt=0;this.groups.set(group,g);this.ensureTimer();
    const payload={requestId:id,campaignId:l.campaign,nmId:body.nmId,kind:options.method==='DELETE'?'cluster_remove':l.kind,
      ...(l.kind==='product'?{placement:body.placement}:{normQuery:body.normQuery}),
      ...(options.method==='DELETE'?{}:{bidKopecks:body.bidKopecks}),confirm:body.confirm===true};
    const admit=async()=>{
      try {
        for(let tries=0;;tries++) {
          if(epoch!==this.epoch)throw new Error('Вход изменён; приём задания остановлен.');
          try {const job=await this.request<ServerBidJob>(l.base+'/bid-commands',{method:'POST',body:payload,keepalive:true});
            if(epoch===this.epoch)this.accept(job,l,key,localId,value);break;
          } catch(e) {
            // Only this idempotent DB admission is replayed, with the very same UUID and payload.
            // There is no browser call to a WB mutation endpoint.
            if(tries>=12||e instanceof ApiError&&(e.status>0&&e.status<500||e.code==='session_unavailable'))throw e;
            await this.wait(Math.min(1000*(tries+1),5000));
          }
        }
      } catch(e) {
        const p=this.pendingJobs.get(id);this.pendingJobs.delete(id);p?.reject(e);
        if(epoch===this.epoch&&this.states.get(key)?.id===localId){this.states.set(key,{id:localId,phase:'failed',value,message:e instanceof Error?e.message:'Приём сервером не подтверждён.'});this.emit();}
      } finally {this.pendingChanged(--this.admissions);this.prune();}
    };
    const prior=this.lanes.get(l.base)??Promise.resolve(),admission=prior.then(admit,admit);
    this.lanes.set(l.base,admission);void admission.finally(()=>{if(this.lanes.get(l.base)===admission)this.lanes.delete(l.base);});
    void promise.finally(()=>{if(this.latest.get(key)?.promise===promise)this.latest.delete(key);}).catch(()=>{});
    return promise as Promise<T>;
  }
  private accept(job:ServerBidJob,l:ReturnType<typeof location>,key?:string,localId?:number,value?:string) {
    const path=l.base+'/campaigns/'+job.campaignId+(job.kind==='product'?'/bids':'/cluster-bids');
    const body={nmId:job.nmId,...(job.kind==='product'?{placement:job.selector}:{normQuery:job.selector}),
      ...(job.kind==='cluster_remove'?{}:{bidKopecks:job.bidKopecks}),...(job.confirm?{confirm:true}:{})};
    key??=bidSaveKey(path,body);value??=JSON.stringify([job.kind==='cluster_remove'?'DELETE':'PATCH',body]);
    const own=this.pendingJobs.get(job.id),seq=BigInt(job.sequence),seen=this.serverSequence.get(key)??-1n;
    const completionKey=JSON.stringify([l.base,l.campaign,l.kind]);
    const confirmedChanged=job.state==='confirmed'&&seq>(this.confirmedGroups.get(completionKey)??0n);
    if(confirmedChanged)this.confirmedGroups.set(completionKey,seq);
    // A poll for an earlier job must not hide a later, still-being-admitted edit.
    const waitingNewer=[...this.pendingJobs.values()].some(p=>p.key===key&&p.localId>(own?.localId??localId??-1));
    if(seq>=seen&&!waitingNewer) {
      this.serverSequence.set(key,seq);
      const phase=job.state==='confirmed'?'applied':['rejected','cancelled'].includes(job.state)?'failed':job.state==='uncertain'?'waiting':job.state==='running'?'sending':'queued';
      this.states.set(key,{id:own?.localId??localId??++this.sequence,phase,value,message:job.message});this.emit();
    }
    else if(confirmedChanged)this.emit();
    if(own&&own.epoch===this.epoch&&['confirmed','rejected','cancelled'].includes(job.state)) {
      this.pendingJobs.delete(job.id);
      if(job.state==='confirmed')own.resolve(job.result??{ok:true,after:job.bidKopecks});
      else own.reject(new ApiError(409,job.code??'bid_rejected',job.message??'Ставка отклонена.'));
    }
  }
  private async pollGroup(key:string,g:Group,force=true):Promise<void> {
    if(g.polling||!force&&(g.nextAt??0)>Date.now())return;g.polling=true;g.nextAt=Date.now()+60000;const epoch=this.epoch;
    try {const r=await this.request<{jobs:ServerBidJob[]}>(g.base+'/bid-commands?campaignId='+encodeURIComponent(g.campaign)+'&kind='+g.kind);
      if(epoch!==this.epoch||this.groups.get(key)!==g)return;
      g.nextAt=Date.now()+(r.jobs.some(j=>['pending','running','uncertain'].includes(j.state))?2000:60000);
      for(const job of [...r.jobs].sort((a,b)=>BigInt(a.sequence)<BigInt(b.sequence)?-1:1))this.accept(job,g);
    } catch { /* A failed read does not erase a committed job or repeat a WB mutation. */ }
    finally {g.polling=false;this.prune();}
  }
  async poll(force=true):Promise<void>{await Promise.all([...this.groups].map(([k,g])=>this.pollGroup(k,g,force)));}
}
const warn=(e:BeforeUnloadEvent)=>{e.preventDefault();e.returnValue='';};
export const bidSaveQueue=new BidSaveQueue(api,pause,undefined,n=>{
  if(typeof window==='undefined')return;
  if(n)window.addEventListener('beforeunload',warn);else window.removeEventListener('beforeunload',warn);
});
export function queueBid<T>(path:string,options:Options):Promise<T>{try{return bidSaveQueue.enqueue<T>(path,options);}catch(e){return Promise.reject(e);}}
export function bidSaveMessage(s:BidSaveState|undefined):string|null {
  if(!s)return null;if(s.phase==='failed'||s.phase==='waiting')return s.message??'Ожидается сверка WB';
  return s.phase==='applied'?'Подтверждено WB':s.phase==='queued'?'В серверной очереди':s.message??'Выполняется сервером…';
}
const channel=typeof window!=='undefined'&&typeof BroadcastChannel!=='undefined'?new BroadcastChannel('bidra-bid-save-session'):null;
if(channel)channel.onmessage=()=>bidSaveQueue.invalidateSession();
export function invalidateBidSaves():void{bidSaveQueue.invalidateSession();channel?.postMessage('session-changed');}
